import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/api/client'
import type { PeriodRecord } from '@/types/models'

const PERIOD_SYMPTOMS = ['腹痛', '腰酸', '头痛', '乳房胀痛', '情绪波动', '疲劳', '失眠', '食欲变化', '恶心', '浮肿']
const PERIOD_MOODS = [
  { emoji: '😊', name: '开心' },
  { emoji: '😢', name: '低落' },
  { emoji: '😤', name: '烦躁' },
  { emoji: '😰', name: '焦虑' },
  { emoji: '😴', name: '困倦' },
  { emoji: '🥰', name: '幸福' },
]

function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : '未知错误'
}

interface PeriodState {
  records: PeriodRecord[]
  spacePeriodRecords: PeriodRecord[]
  visible: boolean
  loading: boolean
  error: string | null
  loadRecords: () => Promise<void>
  loadSpacePeriodData: (spaceId: string) => Promise<void>
  clearSpacePeriodData: () => void
  addRecord: (data: Omit<PeriodRecord, 'id'>) => Promise<boolean>
  updateRecord: (id: string, data: Partial<PeriodRecord>) => Promise<boolean>
  deleteRecord: (id: string) => Promise<void>
  addSpaceRecord: (spaceId: string, data: Omit<PeriodRecord, 'id'>, targetUid?: string) => Promise<boolean>
  updateSpaceRecord: (spaceId: string, id: string, data: Partial<PeriodRecord>) => Promise<boolean>
  deleteSpaceRecord: (spaceId: string, id: string) => Promise<void>
  toggleVisible: () => void
  setVisible: (v: boolean) => void
}

export { PERIOD_SYMPTOMS, PERIOD_MOODS }

export const usePeriodStore = create<PeriodState>()(
  persist(
    (set, get) => ({
      records: [],
      spacePeriodRecords: [],
      visible: true,
      loading: false,
      error: null,

  loadRecords: async () => {
    set({ loading: true, error: null })
    try {
      const data = await api.get<PeriodRecord[]>('/api/period')
      set({ records: Array.isArray(data) ? data : [], loading: false })
    } catch (err) {
      set({ records: [], loading: false, error: getErrorMessage(err) || '加载经期数据失败' })
    }
  },

  loadSpacePeriodData: async (spaceId: string) => {
    try {
      const data = await api.get<PeriodRecord[]>(`/api/spaces/${spaceId}/period-sync-data`)
      if (Array.isArray(data)) {
        set(state => {
          const existing = state.spacePeriodRecords.filter(r => r.spaceId !== spaceId)
          const merged = [...existing, ...data]
          return { spacePeriodRecords: merged }
        })
      }
    } catch {
      // keep existing data
    }
  },

  clearSpacePeriodData: () => set({ spacePeriodRecords: [] }),

  addRecord: async (data) => {
    try {
      const res = await api.post<{ ok: boolean }>('/api/period', data)
      if (res.ok) {
        await get().loadRecords()
        return true
      }
      return false
    } catch (err) {
      const msg = getErrorMessage(err)
      set({ error: msg })
      throw new Error(msg, { cause: err })
    }
  },

  updateRecord: async (id, data) => {
    try {
      const res = await api.patch<{ ok: boolean }>(`/api/period/${id}`, data)
      if (res.ok) {
        await get().loadRecords()
        return true
      }
      return false
    } catch (err) {
      const msg = getErrorMessage(err)
      set({ error: msg })
      throw new Error(msg, { cause: err })
    }
  },

  deleteRecord: async (id) => {
    try {
      await api.delete(`/api/period/${id}`)
      await get().loadRecords()
    } catch (err) {
      const msg = getErrorMessage(err)
      set({ error: msg })
      throw new Error(msg, { cause: err })
    }
  },

  addSpaceRecord: async (spaceId, data, targetUid?) => {
    try {
      const res = await api.post<{ ok: boolean }>(`/api/spaces/${spaceId}/period`, { ...data, targetUid })
      if (res.ok) {
        await get().loadSpacePeriodData(spaceId)
        return true
      }
      return false
    } catch (err) {
      const msg = getErrorMessage(err)
      set({ error: msg })
      throw new Error(msg, { cause: err })
    }
  },

  updateSpaceRecord: async (spaceId, id, data) => {
    try {
      const res = await api.patch<{ ok: boolean }>(`/api/spaces/${spaceId}/period/${id}`, data)
      if (res.ok) {
        await get().loadSpacePeriodData(spaceId)
        return true
      }
      return false
    } catch (err) {
      const msg = getErrorMessage(err)
      set({ error: msg })
      throw new Error(msg, { cause: err })
    }
  },

  deleteSpaceRecord: async (spaceId, id) => {
    try {
      await api.delete(`/api/spaces/${spaceId}/period/${id}`)
      await get().loadSpacePeriodData(spaceId)
    } catch (err) {
      const msg = getErrorMessage(err)
      set({ error: msg })
      throw new Error(msg, { cause: err })
    }
  },

  toggleVisible: () => set(s => ({ visible: !s.visible })),
  setVisible: (v: boolean) => set({ visible: v }),
}),
    {
      name: 'cal_period',
      partialize: (state) => ({ visible: state.visible }),
    }
  )
)
